import { PurchaseDirection } from '@accedo/one-plugin-sdk';
import dayjs from 'dayjs';

import { PackageDefinition, Plan, SubscriptionGroup } from '#/client/models';
import { CardStatus } from '#/plugins/paywall/models';

const EXPIRY_THRESHOLD_IN_DAYS = 30;
const EXPIRY_DATE_FORMAT = /(0[1-9]|1[0-2])\/\d{2}$/;

type Match = { groupId: string; tierIndex: number; productIdentifier: string };

// "change" or "purchase" are the two things this could represent
export type ChangeOrPurchase = {
  // "changes" have direction, or just "purchase"
  action: PurchaseDirection | 'PURCHASE';
  changeFromProductIdentifier?: string;
};

/**
 * Determine if the selected product is a one-time purchase.
 * @param oneTimePurchases The list of one-time purchases.
 * @param selectedProductIdentifier The selected product identifier.
 * @param transformProductIdentifierSafely method for letting the integration transform the identifier for comparison.
 * @returns Whether the selected product is a one-time purchase.
 */
const isOneTimePurchase = (
  oneTimePurchases: PackageDefinition['oneTimePurchases'],
  selectedProductIdentifier: string,
  transformProductIdentifierSafely: (id: string | undefined) => string
) =>
  oneTimePurchases.some(
    ({ web }) =>
      transformProductIdentifierSafely(web?.productIdentifier) ===
      transformProductIdentifierSafely(selectedProductIdentifier)
  );

const findGroupIdAndTierMatches = (
  subscriptionGroups: SubscriptionGroup[],
  currentUserProductIdentifiers: string[],
  selectedProductIdentifier: string,
  transformProductIdentifierSafely: (id: string | undefined) => string
) => {
  const currentMatches: Match[] = [];
  let selectedMatch: Match | undefined;

  subscriptionGroups.forEach((group) => {
    group.tiers.forEach((tier, tierIndex) => {
      tier.plans.forEach(({ web }) => {
        // We need to request the integration to apply any specific logic they may have for comparing IDs (e.g. Cleeng localization IDs)
        const filteredProductIdentifier = transformProductIdentifierSafely(
          web?.productIdentifier
        );

        if (currentUserProductIdentifiers.includes(filteredProductIdentifier)) {
          currentMatches.push({
            groupId: group.id,
            tierIndex,
            productIdentifier: web!.productIdentifier,
          });
        }

        if (filteredProductIdentifier === selectedProductIdentifier) {
          selectedMatch = {
            groupId: group.id,
            tierIndex,
            productIdentifier: web!.productIdentifier,
          };
        }
      });
    });
  });

  return { currentMatches, selectedMatch };
};

/**
 * Find a plan within the subscription groups by its product identifier.
 * @param subscriptionGroups The subscription groups to search.
 * @param productIdentifier The product identifier to match against.
 * @param transformSafely Method for letting the integration transform the identifier for comparison.
 * @returns The matching plan, or undefined if not found.
 */
export const findPlanByProductIdentifier = (
  subscriptionGroups: SubscriptionGroup[],
  productIdentifier: string,
  transformSafely: (id?: string) => string
): Plan | undefined => {
  const normalized = transformSafely(productIdentifier);

  for (const group of subscriptionGroups) {
    for (const tier of group.tiers) {
      for (const plan of tier.plans) {
        if (transformSafely(plan.web?.productIdentifier) === normalized) {
          return plan;
        }
      }
    }
  }

  return undefined;
};

/**
 * Determine the purchase direction based on the package definition, the user's current subscription group, and the new product ID.
 * @param packageDefinition The package definition.
 * @param currentUserProductIdentifiers Array of current user's product IDs.
 * @param selectedProductIdentifier User's selected product ID.
 * @param transformProductIdentifierSafely method for letting the integration transform the identifier for comparison.
 */
export const determinePurchaseOrDirection = (
  packageDefinition: PackageDefinition,
  currentUserProductIdentifiers: string[],
  selectedProductIdentifier: string,
  transformProductIdentifierSafely: (id: string | undefined) => string
): ChangeOrPurchase => {
  const selectedfilteredProductIdentifier = transformProductIdentifierSafely(
    selectedProductIdentifier
  );
  const { subscriptionGroups, oneTimePurchases } = packageDefinition;

  // If the new product is a one-time purchase, it's a purchase.
  if (
    isOneTimePurchase(
      oneTimePurchases,
      selectedProductIdentifier,
      transformProductIdentifierSafely
    )
  ) {
    return { action: 'PURCHASE' };
  }

  // Find the current subscription group and tier index of current subscription groups and the selected product.
  const { selectedMatch, currentMatches } = findGroupIdAndTierMatches(
    subscriptionGroups,
    currentUserProductIdentifiers,
    selectedfilteredProductIdentifier,
    transformProductIdentifierSafely
  );

  // If the selected product don't exist within a subscription group, we throw a error (highly unlikely).
  if (!selectedMatch) {
    throw new Error(
      `No subscription group was found for the selected product ${selectedfilteredProductIdentifier}`
    );
  }

  /**
   * If the selected product exist within the user subscription group, compare the tier indices.
   *  - If the selected product tier is the same as the current product tier, it's a cross-grade.
   *  - If the selected product is a higher tier than the current product tier, it's a upgrade.
   *  - If the selected product is a lower tier than the current product tier, it's a downgrade.
   */
  const current = currentMatches.find(
    (current) => current.groupId === selectedMatch.groupId
  )!;

  if (!current) {
    // If the selected product don't exist within the user subscription group, it's a purchase.
    return { action: 'PURCHASE' };
  }

  if (current.tierIndex === selectedMatch.tierIndex) {
    return {
      action: PurchaseDirection.Cross,
      changeFromProductIdentifier: current.productIdentifier,
    };
  }

  // The tiers are guaranteed to be ordered from most premium to least premium.
  // Example: [Gold, Silver, Bronze]
  // So changing to a lower tier index is an upgrade.
  return {
    action:
      current.tierIndex > selectedMatch.tierIndex
        ? PurchaseDirection.Up
        : PurchaseDirection.Down,
    changeFromProductIdentifier: current.productIdentifier,
  };
};

export const getExpiryStatus = (expiryString: string) => {
  if (!expiryString) {
    return CardStatus.NotFound;
  }

  if (!EXPIRY_DATE_FORMAT.test(expiryString)) {
    console.warn(`Invalid card expiration format: ${expiryString}`);

    return CardStatus.NotFound;
  }

  const now = dayjs();
  const [month, year] = expiryString.split('/');
  const daysLeft = dayjs(`20${year}-${month}-01`).diff(now, 'day');

  if (daysLeft > EXPIRY_THRESHOLD_IN_DAYS) {
    return CardStatus.Valid;
  }

  return daysLeft > 0 ? CardStatus.AboutToExpire : CardStatus.Expired;
};
